import React, { useEffect, useState } from 'react';
import { supabase } from '../services/supabaseClient';
import { AlertTriangle, Database, RefreshCw, WifiOff, X } from 'lucide-react';

export const DatabaseStatus: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'connected' | 'error' | 'needs_setup' | 'offline_mode'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [retryCount, setRetryCount] = useState(0);

  const checkConnection = async () => {
    setStatus('loading');
    try {
      // Intentamos leer la tabla de configuración
      // Usamos count: exact y head: true para que sea una petición muy ligera
      const { error } = await supabase.from('site_config').select('count', { count: 'exact', head: true });

      if (error) {
        // Si el error es "relation does not exist" (código 42P01 en Postgres)
        if (error.message.includes('does not exist') || error.code === '42P01') {
          setStatus('needs_setup');
        } else {
          console.error("Supabase connection error:", error);
          setStatus('error');
          setErrorMessage(error.message);
        }
      } else {
        setStatus('connected');
      }
    } catch (err: any) {
      console.error("Supabase unexpected error:", err);
      setStatus('error');
      setErrorMessage(err.message || 'Error de conexión');
    }
  };

  useEffect(() => {
    checkConnection();
  }, [retryCount]);

  if (status === 'connected' || status === 'offline_mode') return null;
  
  // Si está cargando por más de 1 segundo, podríamos mostrar un spinner, pero mejor no molestar
  if (status === 'loading') return null;

  return (
    <div className={`w-full px-4 py-3 text-sm font-medium flex flex-col sm:flex-row items-center justify-center gap-3 shadow-md transition-colors ${
      status === 'needs_setup' ? 'bg-yellow-50 text-yellow-800 border-b border-yellow-200' : 'bg-red-50 text-red-800 border-b border-red-200'
    }`}>
      {status === 'needs_setup' ? (
        <>
          <Database className="w-5 h-5 flex-shrink-0" />
          <div className="flex-grow text-center sm:text-left">
            <strong>Base de datos no configurada.</strong> Las tablas no existen.
            <span className="block sm:inline sm:ml-1">Ejecuta el script SQL en Supabase.</span>
          </div>
          <button 
            onClick={() => setRetryCount(c => c + 1)}
            className="px-3 py-1 bg-yellow-200 hover:bg-yellow-300 rounded text-yellow-900 text-xs font-bold transition-colors flex items-center gap-1"
          >
            <RefreshCw size={12} /> Reintentar
          </button>
        </>
      ) : (
        <>
          <WifiOff className="w-5 h-5 flex-shrink-0" />
          <div className="flex-grow text-center sm:text-left">
            <strong>Error de conexión:</strong> 
            {errorMessage === 'Failed to fetch' 
              ? ' No se pudo conectar a Supabase. Verifica tu internet o si el proyecto está pausado.' 
              : ` ${errorMessage}`}
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setRetryCount(c => c + 1)}
              className="px-3 py-1 bg-red-200 hover:bg-red-300 rounded text-red-900 text-xs font-bold transition-colors flex items-center gap-1"
            >
              <RefreshCw size={12} /> Reintentar
            </button>
            <button 
              onClick={() => setStatus('offline_mode')}
              className="px-3 py-1 bg-transparent border border-red-200 hover:bg-red-100 rounded text-red-800 text-xs font-bold transition-colors flex items-center gap-1"
              title="Ocultar aviso y usar modo offline"
            >
              <X size={12} /> Ignorar
            </button>
          </div>
        </>
      )}
    </div>
  );
};
